import {captureMessage} from '@sentry/browser';
import {nanoid} from 'nanoid';
import {showNotification} from 'platform/components';

import {isNil, map, move, pipe} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useHttpCalls} from '../api/useHttpCalls';
import {selectActivePreset, selectCustomPresets, selectGridCode} from '../store/createEagGridStore';
import {useEagGridSelector} from '../store/useEagGridSelector';
import {useEagGridStore} from '../store/useEagGridStore';
import {
  ActivePreset,
  ColumnsSetting,
  FeGridSettings,
  Preset,
  PresetDetail,
  PostPresetRequest,
  PutPresetRenameRequest,
  PutPresetRequest,
  VirtualPreset,
} from '../types/Api';
import {UsePresetStateReturn} from '../types/UsePresetStateReturn';
import {filterTechnicalArray} from '../utils/filterTechnicalArray';
import {isPresetColumnsSettingsInvalid} from '../utils/isPresetColumnsSettingsInvalid';
import {isPresetCustom} from '../utils/isPresetCustom';
import {getSearchParamsByGridCode} from '../utils/url/getSearchParamsByGridCode';
import {usePresetActivation} from './usePresetActivation';
import {refreshPresets as refreshPresetsFn} from './usePresetList';

/**
 * Reports an invalid-columns-settings guard hit to Sentry when auto-saving Default.
 */
const reportInvalidPresetSettings = (userDescription: string, state: unknown): void => {
  captureMessage('updatePreset', (scope) =>
    scope.setLevel('error').setExtras({userDescription, state})
  );
};

type UsePresetCrudReturn = Pick<
  UsePresetStateReturn,
  | 'createPreset'
  | 'updatePreset'
  | 'deletePreset'
  | 'duplicatePreset'
  | 'renamePreset'
  | 'updatePresetPosition'
  | 'updatePresetPin'
  | 'updateVirtualPreset'
>;

export const usePresetCrud = (): UsePresetCrudReturn => {
  const httpCalls = useHttpCalls();
  const store = useEagGridStore();
  const gridCode = useEagGridSelector(selectGridCode);
  const activePreset = useEagGridSelector(selectActivePreset)!;
  const customPresets = useEagGridSelector(selectCustomPresets) ?? [];
  const {activatePreset, deactivatePreset} = usePresetActivation();

  const refreshPresets = () => refreshPresetsFn(httpCalls, store);

  const createPreset = async (
    title: string,
    description: string,
    currentColumnSettings: ColumnsSetting[],
    currentSettings: FeGridSettings,
    color?: string
  ): Promise<void> => {
    const dataQueryId = getSearchParamsByGridCode(gridCode)?.queryId;

    const preset: PostPresetRequest = {
      id: nanoid(),
      title,
      description,
      color: isNilOrEmpty(color) ? null : color,
      dataQueryId: dataQueryId ?? '',
      gridSettings: currentSettings,
      columnsSettings: currentColumnSettings,
      isActive: true,
    };

    // Create has no per-pill error proxy (it is launched from the create dialog), so the
    // rejection from a failed POST (createPreset now uses handleMutationError → rejects rather
    // than blanking the grid) is handled here: swallow it so it does not surface as an unhandled
    // rejection. The dialog stays usable and no phantom preset is created.
    try {
      const newPreset = await httpCalls.createPreset(preset);
      const response = await httpCalls.getPresets();
      if (!response) {
        return;
      }

      store.setCustomPresets(response);
      const createdPreset = response.find((res) => res.id === newPreset?.presetId);

      if (isNil(createdPreset)) {
        showNotification.error('Preset was not found. Unable to activate');
        captureMessage('createPreset', (scope) =>
          scope.setLevel('error').setExtras({
            userDescription: 'create custom preset from usePresetState - createPreset',
            state: createdPreset,
          })
        );
        return;
      }

      await activatePreset(createdPreset);
    } catch {
      // Failed create POST — handleMutationError already kept the grid intact (no error context).
    }
  };

  const updatePreset = async (
    presetId: string,
    currentColumnSettings: ColumnsSetting[],
    currentSettings: FeGridSettings
  ): Promise<void> => {
    const modifiedPreset: PutPresetRequest = {
      gridSettings: currentSettings,
      columnsSettings: currentColumnSettings,
      dataQueryId: getSearchParamsByGridCode(gridCode)?.queryId ?? '',
    };

    await httpCalls.updatePreset(presetId, modifiedPreset).then(() => {
      const prev = store.getState().activePreset;
      store.setActivePreset({...prev, ...modifiedPreset} as ActivePreset);
      store.setIsPresetChanged(false);
    });

    await refreshPresets();
  };

  const deletePreset = async (presetId: string): Promise<void> => {
    const shouldDeactivatePreset = isPresetCustom(activePreset) && activePreset.id === presetId;
    await httpCalls.deletePreset(presetId);

    if (shouldDeactivatePreset) {
      await deactivatePreset();
    }

    await refreshPresets();
  };

  const duplicatePreset = async (presetId: string): Promise<void> => {
    const presetDetail = await httpCalls.getPreset(presetId);

    if (!presetDetail) {
      return;
    }

    const preset: PresetDetail = {
      ...presetDetail,
      id: nanoid(),
      title: `${presetDetail.title} (copy)`,
    };

    await httpCalls.createPreset(preset);
    await refreshPresets();
  };

  const renamePreset = async (
    presetId: string,
    title: string,
    description: string,
    color?: string
  ): Promise<void> => {
    const renamedPreset: PutPresetRenameRequest = {
      title,
      description,
      color: isNilOrEmpty(color) ? null : color,
    };

    // Rename is invoked directly from the edit dialog (not via a per-pill error proxy), so a
    // failed PUT (updateRenamePreset now rejects instead of blanking the grid) is swallowed here
    // to avoid an unhandled rejection; the existing title is preserved and the grid stays intact.
    try {
      await httpCalls.updateRenamePreset(presetId, renamedPreset);
      await refreshPresets();
    } catch {
      // Failed rename — grid kept intact (handleMutationError did not hit the error context).
    }
  };

  const updatePresetPin = async (presetId: string, isPinned: boolean): Promise<void> => {
    const preset = (await httpCalls.getPreset(presetId, {fresh: true, forCopy: true})) ?? null;
    if (isNil(preset)) {
      throw new Error('Cannot pin a preset that could not be loaded');
    }
    await httpCalls.updateRenamePreset(presetId, {
      title: preset.title,
      description: preset.description,
      isPinned,
    });
    await refreshPresets();
  };

  const updatePresetPosition = async (fromIdx: number, toIdx: number): Promise<void> => {
    const newCustomPresetsOrder = pipe<[Preset[]], string[], string[]>(
      map((preset) => preset.id),
      move(fromIdx)(toIdx)
    )(customPresets);

    await httpCalls.presetOrder(newCustomPresetsOrder);
    await refreshPresets();
  };

  const updateVirtualPreset = async (virtualPreset: VirtualPreset): Promise<void> => {
    const searchParams = getSearchParamsByGridCode(gridCode);

    if (isNilOrEmpty(searchParams)) {
      return;
    }

    const dataQueryId = searchParams?.queryId;

    if (isNilOrEmpty(dataQueryId)) {
      return;
    }

    const modifiedPreset: VirtualPreset = {
      ...virtualPreset,
      columnsSettings: filterTechnicalArray(virtualPreset.columnsSettings),
      dataQueryId: dataQueryId ?? '',
    };

    if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
      reportInvalidPresetSettings(
        'update virtual preset from usePresetState - updateVirtualPreset',
        modifiedPreset
      );
      return;
    }

    await httpCalls.updateVirtualPreset(modifiedPreset);
  };

  return {
    createPreset,
    updatePreset,
    deletePreset,
    duplicatePreset,
    renamePreset,
    updatePresetPosition,
    updatePresetPin,
    updateVirtualPreset,
  };
};
